import { DatePipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Injectable,
  Input,
  LOCALE_ID,
  model,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
} from '@angular/forms';
import {
  NgbDatepickerModule,
  NgbDateStruct,
  NgbTimeStruct,
  NgbTimepickerModule,
  NgbPopoverModule,
  NgbPopover,
  NgbInputDatepickerConfig,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LocalizedDatePipe } from '../../pipes/localized-date.pipe';
import { PopoverService } from './popover.service';

@Component({
  selector: 'app-date',
  standalone: true,
  imports: [
    TranslateModule,
    NgbPopoverModule,
    NgbDatepickerModule,
    NgbTimepickerModule,
    FormsModule,
    NgClass,
    LocalizedDatePipe,
  ],
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.scss'],
  providers: [
    DatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateComponent),
      multi: true,
    },
  ],
})
export class DateComponent
  implements ControlValueAccessor, Validator, OnDestroy, AfterViewInit {
  @Input() inputType: 'date' | 'time' | 'datetime' = 'date';
  @Input() label: string = 'label';
  @Input() minDate: string | null = null; // Minimum allowed date (ISO string)
  @Input() maxDate: string | null = null; // Maximum allowed date (ISO string)
  @Input() showTimePicker: boolean = false; // Flag to show or hide time picker
  showInput: boolean = false;
  model: NgbDateStruct | null = null;
  timeModel: NgbTimeStruct | null = {
    hour: 0,
    minute: 0,
    second: 0,
  }; // Time picker model
  minNgbDate: NgbDateStruct | null = null; // Converted min date to NgbDateStruct
  maxNgbDate: NgbDateStruct | null = null; // Converted max date to NgbDateStruct
  currentDate: string = '';
  subscriptions = new Subscription();
  isArabic: boolean = true;
  translateService = inject(TranslateService);
  @Output() OnDateSaved = new EventEmitter<string>();
  @Output() OnDateCanceled = new EventEmitter<string>();
  popoverService = inject(PopoverService);

  constructor(private el: ElementRef, private ngbInputDatepickerConfig: NgbInputDatepickerConfig) {
    this.isArabic =
      this.translateService.getDefaultLang() == 'en' ? false : true;
    this.subscriptions.add(
      this.translateService.onDefaultLangChange.subscribe((res) => {
        if (res) {
          this.isArabic = res.lang == 'en' ? false : true;
        }
      })
    );

    this.ngbInputDatepickerConfig.container = 'body'
  }

  ngAfterViewInit(): void {
    const inputs = this.el.nativeElement.querySelectorAll('.ngb-tp-input');
    inputs.forEach((input: HTMLInputElement) => {
      input.placeholder = ''; // Set placeholder to an empty string
    });
  }
  ngOnInit(): void {
    // Convert minDate and maxDate to NgbDateStruct if provided
    if (this.minDate) {
      this.minNgbDate = this.convertIsoStringToNgbDateStruct(this.minDate);
    }
    if (this.maxDate) {
      this.maxNgbDate = this.convertIsoStringToNgbDateStruct(this.maxDate);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  writeValue(val: string | NgbDateStruct | null): void {

    if (typeof val === 'string' && val.length > 10) {
      const date = new Date(val);

      if (!isNaN(date.getTime())) {
        this.currentDate = val;
        this.model = this.convertIsoStringToNgbDateStruct(val);
        this.timeModel = {
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: 0,
        };
      } else {
        this.model = null;
        this.timeModel = null;
        // this.currentDate = this.translateService.instant(
        //   'dateInput.selectDate'
        // );
      }
    } else if (val && this.isNgbDateStruct(val)) {
      this.model = val;
    } else if (val === null) {
      this.model = null;
      this.timeModel = null;
      // this.currentDate = this.translateService.instant('dateInput.selectDate');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private onChange: (value: string | null) => void = () => { };
  private onTouched: () => void = () => { };

  setDisabledState?(isDisabled: boolean): void { }

  validate(control: FormControl) {
    if (this.model) {
      const date = new Date(
        this.model.year,
        this.model.month - 1,
        this.model.day
      );
      if (
        (this.minNgbDate &&
          date <
          new Date(
            this.minNgbDate.year,
            this.minNgbDate.month - 1,
            this.minNgbDate.day
          )) ||
        (this.maxNgbDate &&
          date >
          new Date(
            this.maxNgbDate.year,
            this.maxNgbDate.month - 1,
            this.maxNgbDate.day
          ))
      ) {
        return { invalidDate: { valid: false } };
      }
    }
    return this.model ? null : { invalidDate: { valid: false } };
  }

  onDateChange(value: NgbDateStruct) {
    this.model = value;
    //this.propagateChange();
  }

  onTimeChange(value: NgbTimeStruct) {
    this.timeModel = value;
    //this.propagateChange();
  }

  // Helper method to convert an ISO string to NgbDateStruct
  private convertIsoStringToNgbDateStruct(
    isoString: string
  ): NgbDateStruct | null {
    const date = new Date(isoString);
    if (!isNaN(date.getTime())) {
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      };
    }
    return null;
  }

  private propagateChange() {
    if (this.model) {
      const year = this.model.year;
      const month = String(this.model.month).padStart(2, '0');
      const day = String(this.model.day).padStart(2, '0');

      let hours = '00';
      let minutes = '00';
      let seconds = '00';

      if (this.timeModel) {
        hours = String(this.timeModel.hour).padStart(2, '0');
        minutes = String(this.timeModel.minute).padStart(2, '0');
        seconds = String(this.timeModel.second).padStart(2, '0');
      }

      const localDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

      this.currentDate = localDateTime;

      this.onChange(this.currentDate);
    } else {
      this.onChange(null);
    }
    this.onTouched();
  }
  togglePopover(popover: NgbPopover) {
    this.popoverService.registerPopover(popover);
  }

  selectDate(popover: NgbPopover) {
    this.propagateChange();
    const localDate = new Date(this.currentDate);
    const utcDateTime = localDate.toISOString();
    this.OnDateSaved.emit(utcDateTime);

    setTimeout(() => {
      popover.close();
    }, 500);
  }
  loadInput() {
    const hourContainers =
      this.el.nativeElement.querySelectorAll('.ngb-tp-hour');
    const minuteContainers =
      this.el.nativeElement.querySelectorAll('.ngb-tp-minute');

    minuteContainers.forEach((container: HTMLElement) => {
      const input = container.querySelector(
        '.ngb-tp-input'
      ) as HTMLInputElement;
      if (input) {
        input.placeholder = this.translateService.instant('dateInput.mm');
      }
    });
    hourContainers.forEach((container: HTMLElement) => {
      const input = container.querySelector(
        '.ngb-tp-input'
      ) as HTMLInputElement;
      if (input) {
        input.placeholder = this.translateService.instant('dateInput.hh');
      }
    });
  }

  canceled(dateInputPopover: NgbPopover) {
    dateInputPopover.close();
    setTimeout(() => {
      this.OnDateCanceled.emit();
    }, 500);
  }

  // Custom type guard to check if value is NgbDateStruct
  private isNgbDateStruct(value: any): value is NgbDateStruct {
    return (
      value &&
      typeof value === 'object' &&
      'year' in value &&
      'month' in value &&
      'day' in value
    );
  }

}
